<template>
  <div class="consent-structure-tree">
    <!-- Loading state -->
    <div
      v-if="loading"
      class="consent-structure-tree__loading"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <v-skeleton-loader
        type="list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar"
        class="consent-structure-tree__skeleton"
      />
      <div class="consent-structure-tree__loading-text">
        Loading consent structure...
      </div>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="consent-structure-tree__error"
      role="alert"
      aria-live="assertive"
    >
      <v-icon
        size="48"
        color="error"
        aria-hidden="true"
      >
        mdi-alert-circle-outline
      </v-icon>
      <div class="consent-structure-tree__error-title">
        Failed to load consent structure
      </div>
      <div class="consent-structure-tree__error-message">
        {{ error }}
      </div>
      <v-btn
        color="primary"
        outlined
        class="consent-structure-tree__error-retry"
        @click="handleRetry"
      >
        <v-icon left>
          mdi-refresh
        </v-icon>
        Retry
      </v-btn>
    </div>

    <!-- Content (when not loading or error) -->
    <template v-else>
      <!-- Legend showing node types -->
      <div
        v-if="showLegend"
        class="consent-structure-tree__legend"
      >
        <span class="consent-structure-tree__legend-item">
          <v-icon
            small
            class="syrenis-black--text text--lighten-2"
          >
            mdi-web
          </v-icon>
          Brand
        </span>
        <span class="consent-structure-tree__legend-item">
          <v-icon
            small
            class="prussian-blue--text"
          >
            mdi-circle
          </v-icon>
          Parent Channel
        </span>
        <span class="consent-structure-tree__legend-item">
          <v-icon
            small
            class="black-olive--text text--lighten-2"
          >
            mdi-circle
          </v-icon>
          Child Channel
        </span>
        <span class="consent-structure-tree__legend-item">
          <v-icon
            small
            class="onyx--text text--lighten-1"
          >
            mdi-note-text
          </v-icon>
          Statement
        </span>
        <span class="consent-structure-tree__legend-item">
          <v-icon
            small
            class="cadet-gray--text"
          >
            mdi-translate
          </v-icon>
          Statement Translation
        </span>
      </div>

      <!-- Expand/Collapse controls -->
      <div
        v-if="showExpandControls"
        class="consent-structure-tree__controls"
      >
        <span class="consent-structure-tree__expand-label">
          {{ isExpanded ? 'Collapse All' : 'Expand All' }}
        </span>
        <v-btn
          icon
          small
          :aria-label="isExpanded ? 'Collapse all nodes' : 'Expand all nodes'"
          @click="toggleExpandAll"
        >
          <v-icon>
            {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </v-btn>
      </div>

      <!-- Bulk selection controls -->
      <div
        v-if="showBulkActions && selectable"
        class="consent-structure-tree__bulk-controls"
      >
        <div class="consent-structure-tree__bulk-controls-left">
          <v-btn
            text
            small
            color="primary"
            @click="selectAll"
          >
            <v-icon
              left
              small
            >
              mdi-checkbox-multiple-marked
            </v-icon>
            Select All
          </v-btn>
          <v-btn
            text
            small
            color="primary"
            @click="deselectAll"
          >
            <v-icon
              left
              small
            >
              mdi-checkbox-multiple-blank-outline
            </v-icon>
            Deselect All
          </v-btn>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn
                text
                small
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  left
                  small
                >
                  mdi-filter-variant
                </v-icon>
                Select by Type
                <v-icon
                  right
                  small
                >
                  mdi-chevron-down
                </v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="selectByType(ConsentNodeType.BRAND)">
                <v-list-item-icon>
                  <v-icon small>
                    mdi-web
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>Brands</v-list-item-title>
              </v-list-item>
              <v-list-item @click="selectByType(ConsentNodeType.CHANNEL)">
                <v-list-item-icon>
                  <v-icon small>
                    mdi-circle
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>Channels</v-list-item-title>
              </v-list-item>
              <v-list-item @click="selectByType(ConsentNodeType.STATEMENT)">
                <v-list-item-icon>
                  <v-icon small>
                    mdi-note-text
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>Statements</v-list-item-title>
              </v-list-item>
              <v-list-item @click="selectByType(ConsentNodeType.STATEMENT_TRANSLATION)">
                <v-list-item-icon>
                  <v-icon small>
                    mdi-translate
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>Translations</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <!-- Bulk action toolbar (shows when items are selected) -->
      <v-slide-y-transition>
        <div
          v-if="showBulkActions && selectedNodes.length > 0"
          class="consent-structure-tree__action-bar"
        >
          <div class="consent-structure-tree__action-bar-info">
            <v-icon
              small
              class="mr-2"
            >
              mdi-checkbox-marked-circle
            </v-icon>
            <span class="consent-structure-tree__selection-count">
              {{ selectedNodes.length }} {{ selectedNodes.length === 1 ? 'item' : 'items' }} selected
            </span>
          </div>
          <div class="consent-structure-tree__action-bar-actions">
            <slot
              name="bulk-actions"
              :selected-nodes="selectedNodes"
              :selected-count="selectedNodes.length"
            >
              <v-btn
                text
                small
                color="primary"
                @click="handleBulkAction('export')"
              >
                <v-icon
                  left
                  small
                >
                  mdi-export
                </v-icon>
                Export
              </v-btn>
              <v-btn
                text
                small
                color="primary"
                @click="handleBulkAction('edit')"
              >
                <v-icon
                  left
                  small
                >
                  mdi-pencil
                </v-icon>
                Edit
              </v-btn>
              <v-btn
                text
                small
                color="error"
                @click="handleBulkAction('delete')"
              >
                <v-icon
                  left
                  small
                >
                  mdi-delete
                </v-icon>
                Delete
              </v-btn>
            </slot>
          </div>
        </div>
      </v-slide-y-transition>

      <!-- Tree view -->
      <v-treeview
        ref="treeview"
        v-model="internalSelection"
        :items="items"
        :search="searchQuery"
        :open-all="isExpanded"
        :activatable="activatable"
        :selectable="selectable"
        :dense="dense"
        :hoverable="hoverable"
        :return-object="returnObject"
        item-key="id"
        item-text="name"
        item-children="children"
        class="consent-structure-tree__treeview"
        :aria-label="ariaLabel"
        @update:active="handleActiveUpdate"
        @update:open="handleOpenUpdate"
      >
        <template #label="{ item }">
          <consent-node-label
            :node="item"
            :allow-brand-click="allowBrandClick"
            @click="handleNodeClick"
          />
        </template>

      </v-treeview>

      <!-- Empty state -->
      <div
        v-if="typedItems.length === 0"
        class="consent-structure-tree__empty"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <v-icon
          size="48"
          color="grey lighten-1"
          aria-hidden="true"
        >
          mdi-file-tree-outline
        </v-icon>
        <div class="consent-structure-tree__empty-title">
          No consent structure found
        </div>
        <div class="consent-structure-tree__empty-subtitle">
          Configure brands, channels, and statements to see them here
        </div>
      </div>

      <!-- No results state (when searching) -->
      <div
        v-if="typedItems.length > 0 && searchQuery && filteredCount === 0"
        class="consent-structure-tree__empty"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <v-icon
          size="48"
          color="grey lighten-1"
          aria-hidden="true"
        >
          mdi-magnify-close
        </v-icon>
        <div class="consent-structure-tree__empty-title">
          No matching results
        </div>
        <div class="consent-structure-tree__empty-subtitle">
          Try adjusting your search query
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'
import ConsentNodeLabel from './ConsentNodeLabel.vue'
import { ConsentNodeType } from './types'
import type { ConsentStructureNode, NodeClickEvent, VuetifyTreeviewRef } from './types'

export default Vue.extend({
  name: 'ConsentStructureTree',

  components: {
    ConsentNodeLabel
  },

  props: {
    /** Array of consent structure nodes (hierarchical) */
    items: {
      type: Array as PropType<ConsentStructureNode[]>,
      default: () => [],
      validator(value: unknown): boolean {
        if (!Array.isArray(value)) {
          return false
        }

        /**
         * Sanitizes a string for safe console logging (truncates and escapes)
         */
        const sanitize = (str: unknown, maxLen = 50): string => {
          if (typeof str !== 'string') return '[non-string]'
          return str.slice(0, maxLen).replace(/[<>&"']/g, '')
        }

        /**
         * Recursively validates a node and its children
         * Ensures all required fields are present and valid
         */
        const validateNode = (node: any, path = ''): boolean => {
          // Check node is an object
          if (!node || typeof node !== 'object') {
            console.error(`ConsentStructureTree: Invalid node at ${sanitize(path)}`)
            return false
          }

          // Validate required string fields
          if (typeof node.id !== 'string' || node.id.trim() === '') {
            console.error(`ConsentStructureTree: Node at ${sanitize(path)} missing valid id`)
            return false
          }

          const safeId = sanitize(node.id)

          if (typeof node.name !== 'string' || node.name.trim() === '') {
            console.error(`ConsentStructureTree: Node at ${sanitize(path)} (id: ${safeId}) missing valid name`)
            return false
          }

          // Validate type is a valid ConsentNodeType enum value (1-4)
          if (typeof node.type !== 'number' || ![1, 2, 3, 4].includes(node.type)) {
            console.error(
              `ConsentStructureTree: Node at ${sanitize(path)} (id: ${safeId}) has invalid type. Expected 1-4, got: ${typeof node.type === 'number' ? node.type : 'non-number'}`
            )
            return false
          }

          // Validate icon and color fields exist
          if (typeof node.icon !== 'string' || node.icon === '') {
            console.error(`ConsentStructureTree: Node at ${sanitize(path)} (id: ${safeId}) missing icon`)
            return false
          }

          if (typeof node.color !== 'string' || node.color === '') {
            console.error(`ConsentStructureTree: Node at ${sanitize(path)} (id: ${safeId}) missing color`)
            return false
          }

          // Recursively validate children if present
          if (node.children !== undefined) {
            if (!Array.isArray(node.children)) {
              console.error(
                `ConsentStructureTree: Node at ${sanitize(path)} (id: ${safeId}) children must be an array`
              )
              return false
            }

            const safeName = sanitize(node.name, 30)
            return node.children.every((child: any, index: number) =>
              validateNode(child, `${path}/${safeName}[${index}]`)
            )
          }

          return true
        }

        return value.every((node, index) => validateNode(node, `root[${index}]`))
      }
    },
    /** Search query to filter visible nodes */
    searchQuery: {
      type: String,
      default: ''
    },
    /**
     * Initial expand state for all nodes.
     * This sets the initial state - subsequent changes are controlled internally
     * via the expand/collapse button. Use toggleExpandAll() method for programmatic control.
     */
    expandAll: {
      type: Boolean,
      default: false
    },
    /** Whether to show the legend */
    showLegend: {
      type: Boolean,
      default: true
    },
    /** Whether to show expand/collapse controls */
    showExpandControls: {
      type: Boolean,
      default: true
    },
    /** Whether nodes are activatable (single selection) */
    activatable: {
      type: Boolean,
      default: true
    },
    /** Whether nodes are selectable (checkbox selection) */
    selectable: {
      type: Boolean,
      default: false
    },
    /** Whether to show bulk action controls */
    showBulkActions: {
      type: Boolean,
      default: false
    },
    /** Whether to use dense layout */
    dense: {
      type: Boolean,
      default: true
    },
    /** Whether nodes should be hoverable */
    hoverable: {
      type: Boolean,
      default: true
    },
    /** Whether to return full objects instead of just keys */
    returnObject: {
      type: Boolean,
      default: false
    },
    /** Whether to allow clicking on brand nodes */
    allowBrandClick: {
      type: Boolean,
      default: false
    },
    /** Accessible label for the tree */
    ariaLabel: {
      type: String,
      default: 'Consent structure tree'
    },
    /** Whether the tree is in a loading state */
    loading: {
      type: Boolean,
      default: false
    },
    /** Error message to display (null/empty string means no error) */
    error: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      ConsentNodeType,
      isExpanded: false,
      openNodes: [] as string[],
      internalSelection: [] as string[],
      /**
       * Memoization cache for node lookups by ID.
       * Maps node ID to the actual node reference for O(1) lookup time.
       * Rebuilt when items prop changes to maintain correctness.
       */
      nodeCache: new Map<string, ConsentStructureNode>()
    }
  },

  watch: {
    /**
     * Rebuild the node cache whenever items change.
     * This ensures the cache stays synchronized with the tree structure.
     */
    items: {
      handler(): void {
        this.rebuildNodeCache()
      },
      immediate: true
    },
    /**
     * Watch internal selection and emit events
     */
    internalSelection: {
      handler(newSelection: string[]): void {
        this.handleSelectionChange(newSelection)
      }
    }
  },

  computed: {
    /**
     * Typed accessor for items prop (workaround for Vue 2 validator type inference)
     */
    typedItems(): ConsentStructureNode[] {
      return this.items as ConsentStructureNode[]
    },

    /**
     * Counts the number of nodes that match the search query
     */
    filteredCount(): number {
      if (!this.searchQuery) {
        return this.countNodesRecursive(this.typedItems)
      }

      const query = this.searchQuery.toLowerCase()
      return this.countNodesRecursive(this.typedItems, (node) =>
        node.name.toLowerCase().includes(query)
      )
    },

    /**
     * Returns array of selected node objects
     */
    selectedNodes(): ConsentStructureNode[] {
      return this.internalSelection
        .map((id) => this.nodeCache.get(id))
        .filter((node): node is ConsentStructureNode => node !== undefined)
    }
  },

  /**
   * Sync initial expandAll prop value on mount.
   * Subsequent prop changes are intentionally ignored to allow
   * internal state management via the expand/collapse button.
   */
  mounted(): void {
    if (this.expandAll) {
      this.isExpanded = true
      this.$nextTick(() => {
        this.expandAllNodes()
      })
    }
  },

  /**
   * Clear the node cache on component destruction to prevent memory leaks.
   */
  beforeDestroy(): void {
    this.nodeCache.clear()
  },

  methods: {
    /**
     * Rebuilds the node cache by recursively indexing all nodes by ID.
     * This provides O(1) lookup time for findNodeById instead of O(n) recursive search.
     * Called automatically when items prop changes via watcher.
     */
    rebuildNodeCache(): void {
      this.nodeCache.clear()
      this.indexNodes(this.typedItems)
    },

    /**
     * Recursively indexes all nodes into the cache Map.
     * Helper method for rebuildNodeCache.
     */
    indexNodes(nodes: ConsentStructureNode[]): void {
      for (const node of nodes) {
        this.nodeCache.set(node.id, node)
        if (node.children && node.children.length > 0) {
          this.indexNodes(node.children)
        }
      }
    },

    /**
     * Counts nodes in the tree recursively with optional predicate filtering.
     * This consolidates the previous countNodes and countMatchingNodes functions.
     *
     * @param nodes - Array of nodes to count
     * @param predicate - Optional filter function. If provided, only nodes matching the predicate are counted.
     * @returns Total count of nodes (and their descendants) matching the criteria
     *
     * @example
     * // Count all nodes
     * const total = this.countNodesRecursive(this.items)
     *
     * @example
     * // Count nodes matching a search query
     * const matches = this.countNodesRecursive(this.items, node =>
     *   node.name.toLowerCase().includes(query)
     * )
     *
     * @example
     * // Count only statement nodes
     * const statements = this.countNodesRecursive(this.items, node =>
     *   node.type === ConsentNodeType.STATEMENT
     * )
     */
    countNodesRecursive(
      nodes: ConsentStructureNode[],
      predicate?: (node: ConsentStructureNode) => boolean
    ): number {
      let count = 0

      for (const node of nodes) {
        // If no predicate provided, count all nodes; otherwise only count if predicate matches
        if (!predicate || predicate(node)) {
          count++
        }

        // Recursively count children
        if (node.children && node.children.length > 0) {
          count += this.countNodesRecursive(node.children, predicate)
        }
      }

      return count
    },

    /**
     * Collects all node IDs recursively
     */
    collectAllNodeIds(nodes: ConsentStructureNode[]): string[] {
      const ids: string[] = []
      for (const node of nodes) {
        ids.push(node.id)
        if (node.children && node.children.length > 0) {
          ids.push(...this.collectAllNodeIds(node.children))
        }
      }
      return ids
    },

    /**
     * Collects node IDs by type
     */
    collectNodeIdsByType(nodes: ConsentStructureNode[], type: ConsentNodeType): string[] {
      const ids: string[] = []
      for (const node of nodes) {
        if (node.type === type) {
          ids.push(node.id)
        }
        if (node.children && node.children.length > 0) {
          ids.push(...this.collectNodeIdsByType(node.children, type))
        }
      }
      return ids
    },

    /**
     * Selects all visible nodes
     */
    selectAll(): void {
      const allIds = this.collectAllNodeIds(this.typedItems)
      this.internalSelection = allIds
      this.$emit('bulk-select', allIds)
    },

    /**
     * Deselects all nodes
     */
    deselectAll(): void {
      this.internalSelection = []
      this.$emit('bulk-select', [])
    },

    /**
     * Selects all nodes of a specific type
     */
    selectByType(type: ConsentNodeType): void {
      const typeIds = this.collectNodeIdsByType(this.typedItems, type)
      this.internalSelection = typeIds
      this.$emit('bulk-select', typeIds)
    },

    /**
     * Handles selection change
     */
    handleSelectionChange(selectedIds: string[]): void {
      const selectedNodes = selectedIds
        .map((id) => this.nodeCache.get(id))
        .filter((node): node is ConsentStructureNode => node !== undefined)

      this.$emit('update:selection', selectedIds)
      this.$emit('selection-change', {
        selectedIds,
        selectedNodes
      })
    },

    /**
     * Handles bulk action button clicks
     */
    handleBulkAction(action: string): void {
      this.$emit('bulk-action', {
        action,
        selectedIds: this.internalSelection,
        selectedNodes: this.selectedNodes
      })
    },

    /**
     * Gets a type-safe reference to the v-treeview component
     */
    getTreeviewRef(): VuetifyTreeviewRef | null {
      const treeview = this.$refs.treeview as unknown as VuetifyTreeviewRef | undefined
      return treeview && typeof treeview.updateAll === 'function' ? treeview : null
    },

    /**
     * Toggles the expand/collapse state for all nodes
     */
    toggleExpandAll(): void {
      this.isExpanded = !this.isExpanded
      const treeview = this.getTreeviewRef()
      if (treeview) {
        treeview.updateAll(this.isExpanded)
      }
      this.$emit('expand-toggle', this.isExpanded)
    },

    /**
     * Expands all nodes programmatically
     */
    expandAllNodes(): void {
      const treeview = this.getTreeviewRef()
      if (treeview) {
        treeview.updateAll(true)
      }
    },

    /**
     * Collapses all nodes programmatically
     */
    collapseAllNodes(): void {
      const treeview = this.getTreeviewRef()
      if (treeview) {
        treeview.updateAll(false)
      }
      this.isExpanded = false
    },

    /**
     * Handles node click events from ConsentNodeLabel
     */
    handleNodeClick(event: NodeClickEvent): void {
      this.$emit('node-click', event.node, event)
    },

    /**
     * Handles active node changes
     */
    handleActiveUpdate(active: string[] | ConsentStructureNode[]): void {
      this.$emit('update:active', active)
      this.$emit('node-select', active)
    },

    /**
     * Handles open/closed state changes
     */
    handleOpenUpdate(open: string[]): void {
      this.openNodes = open
      this.$emit('update:open', open)
    },

    /**
     * Handles retry button click in error state
     */
    handleRetry(): void {
      this.$emit('retry')
    },

    /**
     * Finds a node by ID in the tree using memoized cache.
     * O(1) lookup time vs O(n) recursive search.
     *
     * Performance improvement for large trees:
     * - Before: O(n) worst case, recursive traversal
     * - After: O(1) constant time, direct Map lookup
     *
     * The nodes parameter is kept for backwards compatibility but is now ignored.
     * The cache is automatically maintained via the items watcher.
     */
    findNodeById(_nodes: ConsentStructureNode[], id: string): ConsentStructureNode | null {
      return this.nodeCache.get(id) || null
    },

    /**
     * Expands the tree to reveal a specific node
     */
    revealNode(nodeId: string): void {
      const path = this.findNodePath(this.typedItems, nodeId)
      if (path.length > 0) {
        // Open all parent nodes
        for (let i = 0; i < path.length - 1; i++) {
          if (!this.openNodes.includes(path[i])) {
            this.openNodes.push(path[i])
          }
        }
      }
    },

    /**
     * Finds the path from root to a specific node
     */
    findNodePath(
      nodes: ConsentStructureNode[],
      targetId: string,
      currentPath: string[] = []
    ): string[] {
      for (const node of nodes) {
        const newPath = [...currentPath, node.id]
        if (node.id === targetId) {
          return newPath
        }
        if (node.children) {
          const result = this.findNodePath(node.children, targetId, newPath)
          if (result.length > 0) {
            return result
          }
        }
      }
      return []
    }
  }
})
</script>

<style scoped lang="scss">
@import './theme.scss';

.consent-structure-tree {
  background-color: white;
  border-radius: var(--radius);
}

// Loading state
.consent-structure-tree__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
}

.consent-structure-tree__skeleton {
  width: 100%;
  max-width: 600px;
  margin-bottom: 16px;
}

.consent-structure-tree__loading-text {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 8px;
}

// Error state
.consent-structure-tree__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.consent-structure-tree__error-title {
  margin-top: 16px;
  font-size: 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

.consent-structure-tree__error-message {
  margin-top: 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  max-width: 400px;
}

.consent-structure-tree__error-retry {
  margin-top: 24px;
}

// Legend
.consent-structure-tree__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fafafa;
  border-radius: var(--radius) var(--radius) 0 0;
}

.consent-structure-tree__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
}

// Controls
.consent-structure-tree__controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.consent-structure-tree__expand-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}

// Bulk selection controls
.consent-structure-tree__bulk-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background-color: #fafafa;
}

.consent-structure-tree__bulk-controls-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

// Bulk action toolbar
.consent-structure-tree__action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #e3f2fd;
  border-bottom: 2px solid #1976d2;
}

.consent-structure-tree__action-bar-info {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.87);
}

.consent-structure-tree__selection-count {
  font-weight: 500;
  font-size: 14px;
}

.consent-structure-tree__action-bar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

// Treeview
.consent-structure-tree__treeview {
  padding: 8px 0;
}

// Empty state
.consent-structure-tree__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.consent-structure-tree__empty-title {
  margin-top: 16px;
  font-size: 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.consent-structure-tree__empty-subtitle {
  margin-top: 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.4);
}

/* Theme colours - imported from shared theme.scss */
.consent-structure-tree >>> {
  @include consent-theme-colours;
}

/* Treeview overrides for dense layout */
.consent-structure-tree >>> .v-treeview--dense .v-treeview-node__root {
  min-height: 32px;
}

.consent-structure-tree >>> .v-treeview-node__content {
  margin-left: 0;
}

.consent-structure-tree >>> .v-treeview-node__prepend {
  min-width: 24px;
}
</style>
